import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { of } from 'rxjs';
import { MethodUtils, RxUtils, Tr } from '@utils';
import { useLoggedinUser, useService } from '@hooks';
import { FleetVehicle, fleetVehicleService, VehicleStatus } from '@services';
import { Stack } from '@mui/material';
import { FormStepLayoutProgress, PortalPageTitle } from '@components/portal/common';
import {
	QuestionVehicleDetails,
	QuestionVehicleUsers,
	QuestionVehicleFinance,
	QuestionVehicleDriver,
	QuestionVehicleUsage,
	QuestionVehicleCharging
} from '@components/portal/vehicle-list';

const normalizeDriverAssigned = <T extends Record<string, any>>(data: T) => {
	return data?.['driverAssigned'] ? { driverAssigned: data['driverAssigned'] === '1' } : {};
};
const StepFieldsMapping: string[][] = [
	['plateNumber', 'brand', 'model', 'fuelType', 'siteId', 'segmentId'],
	['driverAssigned', 'driverUsage'],
	['financingType', 'registrationDate', 'leasingStartDate', 'leasingDuration', 'leasingDistance', 'otherDetails'],
	['driverName', 'driverSurname', 'driverEmail'],
	['totalKmYear', 'averageKmDaily', 'longestTripWeekly']
];

interface IStepDefinitionWithContent {
	title: ReactNode;
	content: ReactNode;
}

interface IVehicleManageForm {
	mode: 'add' | 'edit';
}

export const VehicleManageForm: FC<IVehicleManageForm> = ({ mode }) => {
	const navigate = useNavigate();
	const loggedInUser = useLoggedinUser();
	const isAdding = mode === 'add';
	const params = useParams<{ id: string }>();
	const vehiclePrev = useRef<FleetVehicle>();
	const [id, setId] = useState(params.id || '');
	const [step, setStep] = useState(0);
	const [completedSteps, setCompletedSteps] = useState<number[]>();
	const { data: vehicle, error } = useService(() => (id ? fleetVehicleService.get(id) : of()), [id, step]);

	const setStepRanged = (newStep: number) => setStep(Math.max(0, Math.min(STEPS.length - 1, newStep)));
	const prevStep = () => setStepRanged(step - 1);
	const update = async <T extends Record<string, any>>(data: T, vehicleStatus?: VehicleStatus) => {
		return RxUtils.promisify(
			fleetVehicleService.update(id, { id, ...vehicle, vehicleStatus, ...data, ...normalizeDriverAssigned(data) }),
			(d) => MethodUtils.callAndReturnValue(() => setStepRanged(step + 1), d)
		);
	};
	const create = <T extends Record<string, any>>(data: T) => {
		RxUtils.promisify(
			fleetVehicleService.create({ vehicleStatus: VehicleStatus.PENDING, ...data, ...normalizeDriverAssigned(data) }),
			(v) => {
				setId(v.id);
				setStepRanged(step + 1);
			}
		);
	};
	const complete = async <T extends Record<string, any>>(data: T) => update(data, VehicleStatus.COMPLETED).then(() => navigate('..'));
	const updateAndRedirect = async <T extends Record<string, any>>(data: T) => update(data).then(() => navigate('..'));

	const STEPS: IStepDefinitionWithContent[] = [
		...(loggedInUser?.isDriver()
			? []
			: [
					{
						title: <Tr.Portal path="vehicle-list.add-edit-vehicle.step-details" />,
						content: <QuestionVehicleDetails values={vehicle} onNext={(data) => (isAdding ? create(data) : update(data))} />
					},
					{
						title: <Tr.Portal path="vehicle-list.add-edit-vehicle.step-finance" />,
						content: <QuestionVehicleFinance onBack={prevStep} values={vehicle} onNext={update} />
					},
					{
						title: <Tr.Portal path="vehicle-list.add-edit-vehicle.step-users" />,
						content: (
							<QuestionVehicleUsers
								{...(isAdding ? { hideSecondaryButton: true } : { onBack: prevStep })}
								values={vehicle}
								onNext={update}
							/>
						)
					}
			  ]),
		{
			title: <Tr.Portal path="vehicle-list.add-edit-vehicle.step-driver-details" />,
			content: (
				<QuestionVehicleDriver
					hideSecondaryButton={loggedInUser?.isDriver()}
					onBack={prevStep}
					values={vehicle}
					onNext={update}
					onSaveAndReturn={updateAndRedirect}
				/>
			)
		},
		{
			title: <Tr.Portal path="vehicle-list.add-edit-vehicle.step-usage" />,
			content: <QuestionVehicleUsage onBack={prevStep} values={vehicle} onNext={update} />
		},
		{
			title: <Tr.Portal path="vehicle-list.add-edit-vehicle.step-charging" />,
			content: <QuestionVehicleCharging onBack={prevStep} values={vehicle} onNext={complete} />
		}
	];

	useEffect(() => {
		if (!isAdding && !loggedInUser?.isDriver()) {
			const stepsInError = [
				...new Set(
					(vehicle?.validationMetaData || [])
						.map(({ key }) => key)
						.map((key) => StepFieldsMapping.findIndex((fields) => fields.includes(key)))
						.filter((foundStep) => foundStep >= 0) || []
				)
			].sort();
			setCompletedSteps([...new Array(STEPS.length)].map((_, i) => i).filter((index) => !stepsInError.includes(index)));
			!vehiclePrev.current && setStep(stepsInError[0] || 0);
			vehiclePrev.current = vehicle;
		}
	}, [vehicle]);

	if (error) {
		return <Navigate to=".." />;
	}

	return (
		<Stack direction="row" sx={{ height: 1.0, maxHeight: 1.0 }}>
			<Stack direction="column" spacing={10} sx={{ height: 1.0, maxHeight: 1.0, flexGrow: 1, paddingY: 5 }}>
				<PortalPageTitle
					paddingX={3}
					title={
						<Tr.Portal
							path={isAdding ? 'vehicle-list.add-edit-vehicle.add-title' : 'vehicle-list.add-edit-vehicle.edit-title'}
						/>
					}
					subtitle={
						<Tr.Portal
							path={isAdding ? 'vehicle-list.add-edit-vehicle.add-subtitle' : 'vehicle-list.add-edit-vehicle.edit-subtitle'}
						/>
					}
					onBackButtonClick={() => navigate(-1)}
				/>
				{STEPS[step].content}
			</Stack>

			<FormStepLayoutProgress
				step={step}
				{...(isAdding ? { onStepClick: (newStep) => id && setStep(newStep) } : { completedSteps, onStepClick: setStep })}
				sx={{ marginY: 6, marginRight: 4 }}
				steps={STEPS}
			/>
		</Stack>
	);
};
